/**
 * Bottom tab bar persistente del panel del terapeuta (móvil primero). Vive dentro
 * del "device" (480px), como hijo flex al fondo del viewport — no `position:fixed`.
 * Elimina los callejones sin salida por construcción: desde cualquier sección las
 * otras cuatro están a un toque. El indicador de sección activa cubre también las
 * vistas de detalle (la ficha resalta "Pacientes"; la mesa/reporte, "Evaluaciones").
 */
import { Link, useLocation } from "react-router-dom";
import { useT, type MessageKey } from "../i18n/index.js";
import s from "./TabBar.module.css";

interface Tab {
  to: string;
  labelKey: MessageKey;
  icon: string;
  /** ¿Está activa esta pestaña para la ruta actual? (incluye vistas de detalle). */
  active: (path: string) => boolean;
}

const TABS: Tab[] = [
  { to: "/terapeuta", labelKey: "tab.home", icon: "🏠", active: (p) => p === "/terapeuta" },
  {
    to: "/terapeuta/evaluaciones",
    labelKey: "tab.evaluations",
    icon: "📋",
    active: (p) =>
      p.startsWith("/terapeuta/evaluaciones") ||
      p.startsWith("/terapeuta/revision") ||
      p.startsWith("/terapeuta/reporte"),
  },
  {
    to: "/terapeuta/pacientes",
    labelKey: "tab.patients",
    icon: "👥",
    active: (p) =>
      p.startsWith("/terapeuta/pacientes") ||
      p.startsWith("/terapeuta/paciente") ||
      p.startsWith("/terapeuta/vincular"),
  },
  { to: "/terapeuta/creditos", labelKey: "tab.credits", icon: "💳", active: (p) => p.startsWith("/terapeuta/creditos") },
  { to: "/terapeuta/perfil", labelKey: "tab.profile", icon: "👤", active: (p) => p.startsWith("/terapeuta/perfil") },
];

export function TabBar() {
  const t = useT();
  const { pathname } = useLocation();
  return (
    <nav className={s.bar} aria-label={t("tab.aria")}>
      {TABS.map((tab) => {
        const activa = tab.active(pathname);
        return (
          <Link
            key={tab.to}
            to={tab.to}
            className={`${s.tab} ${activa ? s.active : ""}`}
            aria-current={activa ? "page" : undefined}
          >
            <span className={s.icon} aria-hidden="true">
              {tab.icon}
            </span>
            <span className={s.label}>{t(tab.labelKey)}</span>
          </Link>
        );
      })}
    </nav>
  );
}
